/**
 * Background-location orchestrator. This is the piece that keeps GPS, alarms and
 * track recording alive when the screen locks or the app is backgrounded.
 *
 * It watches the pure start/stop decision (safety/backgroundService): the service
 * runs only on the installed Android app with Device GPS selected, and only when
 * the anchor watch is down, a background-relevant safety alarm is armed, or the
 * user's manual override is on. When it should run it:
 *   - asks for notification permission and creates the loud alarm channel,
 *   - shows the one-time battery-optimisation guidance,
 *   - starts the BackgroundGnssSource (native foreground service + persistent
 *     notification) and makes it the active position source, standing the
 *     foreground WebView watch down so there's no double-feed and no gap at the
 *     moment the app backgrounds,
 *   - on each background fix runs the priority alarm checks (anchor drag, then
 *     AIS CPA) and fires a loud notification screen-off.
 * When the last thing disarms it stops the service and hands the seam back to the
 * foreground watch.
 *
 * Graceful degradation: if background LOCATION permission is denied the plugin
 * reports it, we latch that (so we don't loop retrying), warn the user, and fall
 * back cleanly to foreground-only. A denied NOTIFICATION permission just means no
 * screen-off sound; location and track still run, and we note it.
 */
import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { useGnssStore } from '@/store/gnss';
import { usePositionSourceStore } from '@/store/positionSource';
import { useAnchorWatchStore } from '@/store/anchorWatch';
import { useAlarmsStore } from '@/store/alarms';
import { usePilotageStore } from '@/store/pilotage';
import { useChartPlotterStore } from '@/store';
import { useAisStore } from '@/store/ais';
import { useRoutingStore } from '@/store/routing';
import { useBackgroundServiceStore } from '@/store/backgroundService';
import { BackgroundGnssSource } from '@/gnss/backgroundSource';
import { isLost } from '@/ais/targetAge';
import {
  anySafetyAlarmArmed,
  serviceShouldRun,
  serviceRunReason,
  writeWatchFlag,
  evaluateBackgroundAlarms,
  advanceAlarms,
  acknowledgeAlarm,
  clearAllAlarms,
  initAlarmAckState,
  type AlarmAckState,
  type BgAlarmInputs,
  swingRadiusM,
  armNativeAnchor,
  checkAlarmDelivery,
  disarmNativeAnchor,
  stampAlive,
  refreshNativeAnchorLine,
  sendRouteWatch,
  sendAisSnapshot,
  buildRouteWatchSnapshot,
  buildAisSnapshot,
} from '@/safety';
import {
  ensureNotificationPermission,
  initAlarmNotifications,
  fireAlarmNotification,
  clearAlarmNotification,
  registerAlarmAckListener,
  ALARM_NOTIFICATION_IDS,
} from '@/notify/alarmNotifications';

/** The priority alarm keys the background path posts notifications for. */
const BG_ALARM_KEYS = ['anchor', 'aisCpa', 'offTrack', 'waypointArrival'] as const;
type BgNotifyKey = keyof typeof ALARM_NOTIFICATION_IDS;

/**
 * The screen-off acknowledge state, held for the life of the module so both the
 * per-fix sweep and the (event-driven) notification-action listener share one
 * source of truth. Resets on a fresh app start, which is the safe default: a
 * still-standing drag re-alerts rather than staying silently suppressed.
 */
let bgAckState: AlarmAckState = initAlarmAckState();

/** Crew pressed OK / tapped a background alarm notification: silence this episode
 *  and close the popup now. Only this alarm is touched; the anchor watch and every
 *  other alarm stay exactly as armed. */
function acknowledgeBackgroundAlarm(key: string): void {
  bgAckState = acknowledgeAlarm(bgAckState, key);
  void clearAlarmNotification(key as BgNotifyKey);
}

/** Gather the current background-alarm inputs straight from the live stores. */
function collectAlarmInputs(): BgAlarmInputs | null {
  const os = useChartPlotterStore.getState().ownship;
  if (!os.position) return null;
  const aw = useAnchorWatchStore.getState();
  const ais = useAisStore.getState();
  const rs = useRoutingStore.getState();
  const config = useAlarmsStore.getState().config;
  // Drop targets we've stopped hearing from long enough to distrust (lost), the
  // same cut the foreground CPA loop makes. A target silent 6-12 min is a ghost:
  // its position is a guess now, so feeding it in could trip a screen-off
  // "collision risk" against a vessel that isn't there. Stale (still heard from
  // recently enough) targets stay in, matching the live plot.
  const nowMs = Date.now();
  const targets = ais.enabled
    ? Object.values(ais.targets)
        .filter((t) => !isLost(t, nowMs))
        .map((t) => ({
          position: t.position,
          cogDeg: t.cogDeg,
          sogKn: t.sogKn,
          label: t.name || String(t.mmsi),
        }))
    : [];
  return {
    own: { position: os.position, cogDeg: os.cog || os.heading, sogKn: os.sog },
    anchor: aw.anchor
      ? {
          anchor: aw.anchor,
          scopeM: aw.scopeM,
          depthM: aw.depthM,
          bowHeightM: aw.bowHeightM,
          bufferM: aw.bufferM,
          mode: aw.mode,
          zone: aw.zone,
        }
      : null,
    aisTargets: targets,
    config,
    route: {
      position: os.position,
      routes: rs.routes,
      marks: rs.marks,
      followRouteId: rs.followRouteId,
      followFromLegIndex: rs.followFromLegIndex,
      activeLegIndex: rs.activeLegIndex,
      activeRouteId: rs.activeRouteId,
    },
  };
}

/** Run the priority checks for one background fix and fire/clear notifications. */
function handleBackgroundFix(): void {
  // Tell the native watch we are still doing the checking, so it stays quiet and
  // lets the on-screen alarm have it. Throttled inside; the moment these stamps
  // stop, for any reason at all, the native check takes the alarm.
  void stampAlive();
  // Keep the native watch's own pulse in the bug report. Throttled to once a
  // minute inside: this is a diagnostic, not part of the alarm.
  void refreshNativeAnchorLine();
  // Refresh the native side's AIS picture (self-throttled to 30 s). Built fresh
  // from the store each time because targets move; the route snapshot, which
  // changes rarely, is sent from its own effect on change instead.
  {
    const ais = useAisStore.getState();
    const cfg = useAlarmsStore.getState().config;
    const snap = buildAisSnapshot(
      {
        enabled: ais.enabled,
        cpaEnabled: cfg.aisCpaEnabled,
        cpaNm: cfg.aisCpaNm,
        tcpaMin: cfg.aisTcpaMin,
        targets: ais.targets,
      },
      Date.now(),
    );
    void sendAisSnapshot(snap ? JSON.stringify(snap) : null);
  }
  const inputs = collectAlarmInputs();
  if (!inputs) return;
  // Master mute reaches the screen-off path too: while muted we clear any
  // standing alarm notifications and post nothing, so a muted alarm is silent
  // everywhere, not just on-screen. Detection still runs (the anchor watch and
  // the checks above are untouched); this only gates the loud notification.
  if (useAlarmsStore.getState().masterMuted) {
    // Take down whatever is actually up, which after the first muted fix is
    // nothing. This used to cancel all four keys on every fix for as long as the
    // mute stood, so a muted alarm left overnight was a cancel call several
    // times a second all night for notifications that had long gone.
    const cleared = clearAllAlarms(bgAckState, BG_ALARM_KEYS);
    for (const key of cleared.toClear) void clearAlarmNotification(key as BgNotifyKey);
    // Muting ends every episode, so unmuting re-alerts a still-standing condition
    // rather than leaving it silently suppressed by a stale acknowledgement. The
    // posted flags carry across, so we do not go back to sweeping every fix.
    bgAckState = clearAllAlarms(initAlarmAckState(), BG_ALARM_KEYS).state;
    return;
  }
  const hits = evaluateBackgroundAlarms(inputs);
  const hitByKey = new Map(hits.map((h) => [h.key, h] as const));
  // Fold this fix through the acknowledge state: a fresh alarm posts, a still-
  // firing but acknowledged one stays silent (so pressing OK genuinely dismisses
  // it), and a resolved one clears.
  const { state, toNotify, toClear } = advanceAlarms(bgAckState, hitByKey.keys(), BG_ALARM_KEYS);
  bgAckState = state;
  for (const key of toNotify) {
    const h = hitByKey.get(key as (typeof BG_ALARM_KEYS)[number]);
    if (h) void fireAlarmNotification({ title: h.title, body: h.body, key: h.key });
  }
  for (const key of toClear) void clearAlarmNotification(key as BgNotifyKey);
}

export function BackgroundGeolocationRunner() {
  const sourceKind = usePositionSourceStore((s) => s.sourceKind);
  const anchor = useAnchorWatchStore((s) => s.anchor);
  const config = useAlarmsStore((s) => s.config);
  const clearings = usePilotageStore((s) => s.clearings);
  const clearingDepths = usePilotageStore((s) => s.clearingDepths);
  const manualOverride = useBackgroundServiceStore((s) => s.manualOverride);
  const deniedLatch = useBackgroundServiceStore((s) => s.deniedLatch);
  const serviceRunning = useBackgroundServiceStore((s) => s.serviceRunning);

  const isNative = Capacitor.isNativePlatform();
  const safetyAlarmArmed = anySafetyAlarmArmed(config, {
    clearingCount: clearings.length,
    clearingDepthCount: clearingDepths.length,
  });
  const decision = {
    isNative,
    usingDeviceGps: sourceKind === 'device',
    anchorWatchArmed: anchor != null,
    safetyAlarmArmed,
    manualOverride,
  };
  const shouldRun = serviceShouldRun(decision);
  const effectiveRun = shouldRun && !deniedLatch;

  // Leave the Android side a note saying what is depending on us out here.
  //
  // When the renderer is reclaimed, native code is all that is still running and
  // it cannot see into the web app to know whether a watch was on. So the reason
  // goes in a file it can read (safety/watchFlag.ts), and MainActivity says so
  // out loud instead of letting an anchor watch stop in silence behind a
  // notification that still claims it is watching.
  //
  // Depends on the reason STRING, not the decision object, which is rebuilt every
  // render. Writing only happens on a real change.
  const watchReason = effectiveRun ? (serviceRunReason(decision) ?? 'Watch active') : null;
  useEffect(() => {
    void writeWatchFlag(watchReason);
  }, [watchReason]);


  // Hand the anchor to the native watch as well.
  //
  // The check in this file runs in the WebView, and Android takes the renderer
  // from a backgrounded WebGL map without asking. When it did, the location
  // service carried on delivering fixes and nobody was reading them: the blue
  // icon stayed up, the service notification still said the watch was on, and
  // Edd lifted the anchor, drove off, and heard nothing. So the same anchor goes
  // to Java, which keeps checking whether or not this code is running.
  //
  // The boundary is sent as ONE number in metres, already including the buffer,
  // so the two sides cannot drift apart over how a swing radius is worked out.
  const anchorScopeM = useAnchorWatchStore((s) => s.scopeM);
  const anchorDepthM = useAnchorWatchStore((s) => s.depthM);
  const anchorBowM = useAnchorWatchStore((s) => s.bowHeightM);
  const anchorBufferM = useAnchorWatchStore((s) => s.bufferM);
  const anchorMode = useAnchorWatchStore((s) => s.mode);
  const anchorZone = useAnchorWatchStore((s) => s.zone);
  const masterMuted = useAlarmsStore((s) => s.masterMuted);
  useEffect(() => {
    if (!isNative) return;
    if (!anchor) {
      void disarmNativeAnchor();
      return;
    }
    const radiusM =
      swingRadiusM({ scopeM: anchorScopeM, depthM: anchorDepthM, bowHeightM: anchorBowM }) +
      anchorBufferM;
    void armNativeAnchor({
      lat: anchor[1],
      lon: anchor[0],
      radiusM,
      mode: anchorMode,
      zone: anchorZone,
      muted: masterMuted,
    });
  }, [
    isNative,
    anchor,
    anchorScopeM,
    anchorDepthM,
    anchorBowM,
    anchorBufferM,
    anchorMode,
    anchorZone,
    masterMuted,
  ]);

  // Hand the route picture to the native watch: the arrival points and the leg
  // being steered. Same disease, same cure as the anchor: these alarms ran only
  // in freezable JavaScript behind arming screens that implied otherwise. Sent
  // on change (routes change rarely); the AIS picture goes per-fix, throttled,
  // from handleBackgroundFix because targets move constantly.
  const routes = useRoutingStore((s) => s.routes);
  const marks = useRoutingStore((s) => s.marks);
  const followRouteId = useRoutingStore((s) => s.followRouteId);
  const activeLegIndex = useRoutingStore((s) => s.activeLegIndex);
  const activeRouteId = useRoutingStore((s) => s.activeRouteId);
  useEffect(() => {
    if (!isNative) return;
    const snap = buildRouteWatchSnapshot(
      { routes, marks, followRouteId, activeLegIndex, activeRouteId },
      Date.now(),
    );
    void sendRouteWatch(snap ? JSON.stringify(snap) : null);
  }, [isNative, routes, marks, followRouteId, activeLegIndex, activeRouteId]);

  // Wire the notification's OK / tap to the acknowledge path, once, for the life of
  // the app. This is what was missing: with no listener, pressing OK on a screen-off
  // alarm did nothing and it re-fired on the next fix. Native-only; a no-op remover
  // on web, so it's safe to mount unconditionally.
  useEffect(() => {
    let remove = () => {};
    let cancelled = false;
    void registerAlarmAckListener((key) => acknowledgeBackgroundAlarm(key)).then((r) => {
      if (cancelled) r();
      else remove = r;
    });
    return () => {
      cancelled = true;
      remove();
    };
  }, []);

  // Effect A: keep serviceRunning in step with the decision, and do the one-time
  // permission + guidance work when we first come up.
  useEffect(() => {
    const bg = useBackgroundServiceStore.getState();
    if (effectiveRun) {
      if (!bg.serviceRunning) {
        bg.setStatus('requesting', 'Starting background watch…');
        bg.setServiceRunning(true);
        // Best-effort: notification permission + the loud alarm channel. A denial
        // here doesn't stop the service (location + track still run); it just
        // means no screen-off sound, which we note as a soft degrade.
        void (async () => {
          const perm = await ensureNotificationPermission();
          await initAlarmNotifications();
          if (perm === 'denied') {
            bg.setDegraded(
              true,
              'Notifications are off, so a screen-off alarm can vibrate but not show or sound. Allow notifications for Ardent Fix to fix this.',
              { title: 'Alarms may be silent', action: 'notificationSettings' },
            );
            return;
          }
          // Permission granted is not the whole story: a channel the user has
          // silenced (say, after a night of false alarms) passes the permission
          // check and still swallows every alarm. Ask Android the real question
          // now, while the skipper can still fix it, not at three in the morning.
          const found = await checkAlarmDelivery();
          if (found) {
            bg.setDegraded(true, found.problem, {
              // Battery gets its own headline because the failure is different
              // in kind: not a quiet alarm, a dead watch (the 9 Aug drive-off).
              title: found.kind === 'battery' ? 'The watch can be killed' : 'Alarms may be silent',
              action: found.kind === 'battery' ? 'batteryOptimisation' : 'notificationSettings',
            });
          }
        })();
        // The one-time "Allow all the time" location + battery-optimisation
        // guidance is surfaced by BackgroundServiceBanner while the service is up
        // and batteryGuidanceShown is still false; it marks itself shown on
        // dismiss, so we don't nag on every arm.
      }
    } else {
      if (bg.serviceRunning) {
        bg.setServiceRunning(false);
        bg.setStatus('idle');
      }
      // Disarmed entirely: clear the denied latch so the next arm retries, drop
      // any standing degrade warning, and reset the user's dismissal so a genuinely
      // fresh arm can warn again if a permission is still denied.
      if (!shouldRun) {
        if (bg.deniedLatch) bg.setDeniedLatch(false);
        if (bg.degraded) bg.setDegraded(false);
        if (bg.degradedDismissed) bg.resetDegradeDismissed();
      }
    }
  }, [effectiveRun, shouldRun]);

  // Effect B: own the background source lifecycle while the service is up.
  useEffect(() => {
    if (!serviceRunning) return;
    const bg = useBackgroundServiceStore.getState();
    const reason = serviceRunReason(decision) ?? 'Watch active';
    const source = new BackgroundGnssSource({
      notificationTitle: `Ardent Fix — ${reason}`,
      notificationText: 'Keeping GNSS alive for your alarms and track. Tap to open.',
      // Permissions are handled here / by the plugin; let the plugin raise the
      // background-location dialog if it must.
      requestPermissions: true,
      onStatus: (st, msg) => {
        bg.setStatus(st, msg ?? null);
        if (st === 'denied') {
          // Background location refused: latch it, warn, and fall back. The latch
          // flips effectiveRun false, so Effect A stops the service and the
          // foreground watch takes back over.
          bg.setDeniedLatch(true);
          bg.setDegraded(
            true,
            'Background location is off, so alarms and track only work with the app open. Allow "Allow all the time" location for Ardent Fix to keep them working screen-off.',
          );
        } else if (st === 'live') {
          // A clean live fix means we're not degraded on the location front.
          if (bg.degraded && bg.deniedLatch === false) bg.setDegraded(false);
        }
      },
      onLocation: () => handleBackgroundFix(),
    });
    useGnssStore.getState().setSource(source);
    source.start();

    return () => {
      source.stop();
      if (useGnssStore.getState().source === source) {
        useGnssStore.getState().setSource(null);
      }
    };
    // decision is intentionally read once at start; the effect re-runs on
    // serviceRunning, which is what gates the source.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceRunning]);

  return null;
}
